import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  StickyNote, Plus, Search, Star, Users, Network, Folder, Tag,
  LayoutGrid, List as ListIcon, RefreshCw, MoreVertical, Edit3,
  Trash2, RotateCcw, X, CheckCircle2, Globe, Lock, Eye, EyeOff,
  Copy, Link2, Shield, Clock, FileText, BookOpen, Code, ClipboardList,
  BriefcaseBusiness, MessageSquare, Sparkles, ChevronDown, ExternalLink,
  AlertTriangle, Share2, LayoutDashboard, Heart, Pencil, ArrowUpDown,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';
import * as notepadApi from '../../api/notepad';

// ─── Constants ────────────────────────────────────────────
const MAX_CHARS = 10000;

const DEFAULT_CATEGORIES = ['Personal', 'Work', 'Development', 'Code', 'Documentation', 'Important', 'Temporary', 'Custom'];

const EXPIRY_PRESETS = [
  { label: 'Never', value: null },
  { label: '1 minute', value: 1 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '1 day', value: 60 * 24 },
  { label: '5 days', value: 60 * 24 * 5 },
  { label: '30 days', value: 60 * 24 * 30 },
  { label: '1 year', value: 60 * 24 * 365 },
];

const SHARE_EXPIRY_PRESETS = [
  { label: 'Permanent', value: null },
  { label: '1 minute', value: 1 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '1 day', value: 60 * 24 },
  { label: '5 days', value: 60 * 24 * 5 },
  { label: '30 days', value: 60 * 24 * 30 },
  { label: '1 year', value: 60 * 24 * 365 },
];

const TEMPLATE_ICONS = {
  'Simple Note': StickyNote,
  'Meeting Notes': Users,
  'Code Snippet': Code,
  'Documentation': FileText,
  'Checklist': ClipboardList,
  'Project Notes': BriefcaseBusiness,
  'Quick Memo': MessageSquare,
};

// ─── Utilities ────────────────────────────────────────────
function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function countChars(content) {
  return stripHtml(content || '').length;
}

function getAppOrigin() {
  return window.location.origin;
}

function buildPublicUrl(tokenPrefix) {
  // tokenPrefix is first 8 chars; we store the full token on creation
  return tokenPrefix ? `${getAppOrigin()}/n/${tokenPrefix}...` : '';
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function addMinutes(minutes) {
  if (!minutes) return null;
  const d = new Date();
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
}

// ─── Note Editor Modal ─────────────────────────────────────
function NoteEditorModal({ note, categories, templates, onSave, onClose }) {
  const isEdit = !!note?.id;
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [visibility, setVisibility] = useState(note?.visibility || 'PRIVATE');
  const [categoryId, setCategoryId] = useState(note?.category_id || '');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(note?.tags || []);
  const [isFavorite, setIsFavorite] = useState(note?.is_favorite || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(() => {
    if (!note?.publicLink?.expires_at) return null;
    const diff = Math.round((new Date(note.publicLink.expires_at).getTime() - Date.now()) / 60000);
    const matched = EXPIRY_PRESETS.find((p) => p.value && Math.abs(p.value - diff) <= 2);
    return matched ? matched.value : (diff > 0 ? diff : null);
  });
  const [publicLinkSettings, setPublicLinkSettings] = useState(() => ({
    allow_indexing: note?.publicLink?.allow_indexing ?? false,
    allow_copy: note?.publicLink?.allow_copy ?? true,
    allow_edit: note?.publicLink?.allow_edit ?? false,
    show_view_count: note?.publicLink?.show_view_count ?? true,
    allow_comments: note?.publicLink?.allow_comments ?? false,
    allow_download: note?.publicLink?.allow_download ?? false,
  }));
  const [rawToken, setRawToken] = useState(null);
  const textareaRef = useRef(null);

  const charCount = useMemo(() => countChars(content), [content]);
  const charPercent = Math.min((charCount / MAX_CHARS) * 100, 100);
  const charColor = charCount > MAX_CHARS * 0.9 ? '#ef4444' : charCount > MAX_CHARS * 0.75 ? '#f59e0b' : '#10b981';

  const handleContentChange = (e) => {
    const val = e.target.value;
    const count = countChars(val);
    if (count > MAX_CHARS) {
      setError(`Character limit reached: ${count} / ${MAX_CHARS}`);
      return;
    }
    setError('');
    setContent(val);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  };

  const removeTag = (tag) => setTags(tags.filter((t) => t !== tag));

  const applyTemplate = (tmpl) => {
    if (!content || window.confirm('Apply template? This will replace current content.')) {
      setContent(tmpl.content);
      setTitle(title || tmpl.name);
    }
    setShowTemplates(false);
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (charCount > MAX_CHARS) { setError(`Content exceeds ${MAX_CHARS} characters`); return; }
    setSaving(true);
    setError('');
    try {
      const data = {
        title: title.trim(),
        content,
        visibility,
        category_id: categoryId || undefined,
        tags,
        is_favorite: isFavorite,
        ...(visibility === 'PUBLIC' ? {
          public_link_settings: {
            ...publicLinkSettings,
            expires_at: addMinutes(expiryMinutes),
          }
        } : {}),
      };
      const result = await onSave(data, note?.id);
      if (result?.rawToken) setRawToken(result.rawToken);
      else onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside className="card-drawer np-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit Note' : 'New Note'}>
        <div className="card-drawer-header np-drawer-header">
          <div className="card-drawer-header-left">
            <div className="card-drawer-icon-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}>
              <StickyNote size={18} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">{isEdit ? 'Edit Note' : 'New Note'}</h2>
              <p className="card-drawer-subtitle">
                {isEdit ? 'Update your note content & settings' : 'Create an encrypted or public note'}
              </p>
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close drawer"><X size={18} /></button>
        </div>

        {rawToken ? (
          // Token reveal screen
          <div className="card-drawer-body np-drawer-body">
            <div className="np-token-reveal">
              <div className="np-token-icon"><Globe size={32} color="#06b6d4" /></div>
              <h3>Note is now Public!</h3>
              <p className="text-secondary">Your public link has been created. Copy it now — the full token is only shown once.</p>
              <div className="np-token-box">
                <code className="np-token-value">{getAppOrigin()}/n/{rawToken}</code>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                  navigator.clipboard.writeText(`${getAppOrigin()}/n/${rawToken}`);
                }}>
                  <Copy size={14} /> Copy
                </button>
              </div>
              <button type="button" className="btn btn-primary" onClick={onClose} style={{ marginTop: 16 }}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="card-drawer-body np-drawer-body">
              {/* Title */}
              <div className="np-field-group">
                <label className="np-label">Title</label>
                <input
                  type="text"
                  className="np-input"
                  placeholder="Note title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={500}
                />
              </div>

              {/* Template Picker */}
              <div className="np-field-group">
                <button type="button" className="np-template-toggle-btn" onClick={() => setShowTemplates(!showTemplates)}>
                  <Sparkles size={14} /> Apply Template <ChevronDown size={13} className={showTemplates ? 'rotate-180' : ''} />
                </button>
                {showTemplates && (
                  <div className="np-template-grid">
                    {templates.map((tmpl) => {
                      const Icon = TEMPLATE_ICONS[tmpl.name] || StickyNote;
                      return (
                        <button key={tmpl.id} type="button" className="np-template-item" onClick={() => applyTemplate(tmpl)}>
                          <Icon size={15} />
                          <span>{tmpl.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="np-field-group">
                <div className="np-content-header">
                  <label className="np-label">Content</label>
                  <span className="np-char-count" style={{ color: charColor }}>
                    {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                  </span>
                </div>
                <textarea
                  ref={textareaRef}
                  className="np-textarea"
                  placeholder="Start writing..."
                  value={content}
                  onChange={handleContentChange}
                  rows={10}
                />
                <div className="np-char-bar">
                  <div className="np-char-fill" style={{ width: `${charPercent}%`, background: charColor }} />
                </div>
              </div>

              {/* Visibility + Category */}
              <div className="np-field-row">
                <div className="np-field-group np-field-half">
                  <label className="np-label">Visibility</label>
                  <select className="np-select" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                    <option value="PRIVATE">🔒 Private</option>
                    <option value="PUBLIC">🌐 Public</option>
                  </select>
                </div>
                <div className="np-field-group np-field-half">
                  <label className="np-label">Category</label>
                  <select className="np-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    <option value="">No Category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Public Settings */}
              {visibility === 'PUBLIC' && (
                <div className="np-public-settings glass-panel">
                  <div className="np-public-settings-header">
                    <Globe size={15} color="#06b6d4" />
                    <strong>Public Link Settings</strong>
                  </div>
                  <p className="np-public-note">Anyone with the link can view this note. A secure public URL will be generated.</p>

                  <div className="np-field-group">
                    <label className="np-label">Link Expiration</label>
                    <select className="np-select" value={expiryMinutes ?? ''} onChange={(e) => setExpiryMinutes(e.target.value ? Number(e.target.value) : null)}>
                      {EXPIRY_PRESETS.map((p) => (
                        <option key={p.label} value={p.value ?? ''}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="np-toggle-grid">
                    {[
                      { key: 'allow_copy', label: 'Allow Copy' },
                      { key: 'allow_edit', label: 'Allow Write / Edit' },
                      { key: 'show_view_count', label: 'Show View Count' },
                      { key: 'allow_indexing', label: 'Search Engine Indexing' },
                      { key: 'allow_comments', label: 'Allow Comments' },
                      { key: 'allow_download', label: 'Allow Download' },
                    ].map(({ key, label }) => (
                      <label key={key} className="np-toggle-item">
                        <input
                          type="checkbox"
                          checked={publicLinkSettings[key]}
                          onChange={(e) => setPublicLinkSettings({ ...publicLinkSettings, [key]: e.target.checked })}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div className="np-field-group">
                <label className="np-label">Tags</label>
                <div className="np-tags-input-row">
                  <input
                    type="text"
                    className="np-input"
                    placeholder="Add tag and press Enter..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); }}}
                  />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addTag}>Add</button>
                </div>
                {tags.length > 0 && (
                  <div className="np-tags-list">
                    {tags.map((t) => (
                      <span key={t} className="np-tag">
                        #{t}
                        <button type="button" onClick={() => removeTag(t)}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Favorite */}
              <label className="np-toggle-item np-favorite-toggle">
                <input type="checkbox" checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} />
                <Star size={14} color={isFavorite ? '#f59e0b' : undefined} />
                <span>Add to Favorites</span>
              </label>

              {error && <div className="np-error-msg"><AlertTriangle size={13} /> {error}</div>}
            </div>

            <div className="card-drawer-footer np-drawer-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Note')}
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

// ─── Share Drawer ───────────────────────────────────────────
function ShareModal({ note, onClose }) {
  const [shares, setShares] = useState([]);
  const [userId, setUserId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [permission, setPermission] = useState('VIEW');
  const [expiryMinutes, setExpiryMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    notepadApi.getNoteShares(note.id).then((r) => setShares(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [note.id]);

  const handleShare = async () => {
    if (!userId.trim() && !teamId.trim()) { setError('Enter a user email/ID or team ID'); return; }
    setSaving(true);
    setError('');
    try {
      await notepadApi.shareNote(note.id, {
        user_id: userId || undefined,
        team_id: teamId || undefined,
        permission,
        expires_at: addMinutes(expiryMinutes),
      });
      const r = await notepadApi.getNoteShares(note.id);
      setShares(r.data);
      setUserId(''); setTeamId('');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to share note');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (shareId) => {
    await notepadApi.revokeShare(note.id, shareId);
    setShares(shares.filter((s) => s.id !== shareId));
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside className="card-drawer np-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Share Note" style={{ maxWidth: 480 }}>
        <div className="card-drawer-header np-drawer-header">
          <div className="card-drawer-header-left">
            <div className="card-drawer-icon-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}>
              <Share2 size={18} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">Share Note</h2>
              <p className="card-drawer-subtitle">"{note.title}"</p>
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close drawer"><X size={18} /></button>
        </div>
        <div className="card-drawer-body np-drawer-body">
          <p className="text-secondary text-sm" style={{ marginBottom: 14 }}>Share securely with vault users or teams.</p>

          <div className="np-field-group">
            <label className="np-label">User Email or ID</label>
            <input className="np-input" placeholder="user@example.com or UUID" value={userId} onChange={(e) => setUserId(e.target.value)} />
          </div>
          <div className="np-field-row">
            <div className="np-field-group np-field-half">
              <label className="np-label">Permission</label>
              <select className="np-select" value={permission} onChange={(e) => setPermission(e.target.value)}>
                <option value="VIEW">View Only</option>
                <option value="EDIT">Can Edit</option>
              </select>
            </div>
            <div className="np-field-group np-field-half">
              <label className="np-label">Expiration</label>
              <select className="np-select" value={expiryMinutes ?? ''} onChange={(e) => setExpiryMinutes(e.target.value ? Number(e.target.value) : null)}>
                {SHARE_EXPIRY_PRESETS.map((p) => (
                  <option key={p.label} value={p.value ?? ''}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          {error && <div className="np-error-msg"><AlertTriangle size={13} /> {error}</div>}
          <button type="button" className="btn btn-primary" onClick={handleShare} disabled={saving} style={{ marginTop: 6, marginBottom: 18, width: '100%', height: 36 }}>
            {saving ? 'Sharing...' : 'Share Note'}
          </button>

          {loading ? <p className="text-secondary text-sm">Loading shares...</p> : shares.length > 0 ? (
            <div>
              <label className="np-label">Current Shares</label>
              {shares.map((s) => (
                <div key={s.id} className="np-share-row">
                  <div>
                    <span className="np-share-target">{s.user_email || s.team_name || 'Unknown'}</span>
                    <span className="np-share-perm">{s.permission}</span>
                    {s.expires_at && <span className="text-secondary text-sm"> · Expires {formatDate(s.expires_at)}</span>}
                  </div>
                  <button type="button" className="btn-icon" onClick={() => handleRevoke(s.id)} title="Revoke"><X size={14} /></button>
                </div>
              ))}
            </div>
          ) : <p className="text-secondary text-sm">No active shares.</p>}
        </div>
        <div className="card-drawer-footer np-drawer-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </aside>
    </div>
  );
}

// ─── Public Link Drawer ──────────────────────────────────────
function PublicLinkModal({ note, link, onClose, onRegenerate, onRevoke }) {
  const [copied, setCopied] = useState(false);
  const token = link?.rawToken || link?.token || note?.publicLink?.token || '';
  const fullUrl = token ? `${getAppOrigin()}/n/${token}` : '';

  const handleCopy = () => {
    if (fullUrl) {
      navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside className="card-drawer np-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Public Link" style={{ maxWidth: 480 }}>
        <div className="card-drawer-header np-drawer-header">
          <div className="card-drawer-header-left">
            <div className="card-drawer-icon-badge" style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}>
              <Globe size={18} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">Public Link</h2>
              <p className="card-drawer-subtitle">"{note.title}"</p>
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close drawer"><X size={18} /></button>
        </div>
        <div className="card-drawer-body np-drawer-body">
          <div className="np-public-url-box">
            <Globe size={14} color="#06b6d4" style={{ flexShrink: 0 }} />
            <code className="np-public-url-text">{fullUrl || 'No active public link'}</code>
          </div>
          {(note?.publicLink?.allow_edit || link?.allow_edit) && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, background: 'rgba(16,185,129,0.15)', color: '#10b981', fontSize: '0.78rem', fontWeight: 600, marginBottom: 12 }}>
              <Edit3 size={12} /> Anyone with link can write/edit
            </div>
          )}
          <div className="np-public-link-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopy} disabled={!fullUrl}>
              {copied ? <><CheckCircle2 size={13} /> Copied!</> : <><Copy size={13} /> Copy Link</>}
            </button>
            {fullUrl && (
              <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                <ExternalLink size={13} /> Open
              </a>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={onRegenerate}>
              <RefreshCw size={13} /> Regenerate
            </button>
            <button type="button" className="btn btn-danger btn-sm" onClick={onRevoke}>
              <EyeOff size={13} /> Disable
            </button>
          </div>
          <p className="text-secondary text-sm np-link-note">
            Regenerating the link immediately invalidates the old one. Anyone with the URL can access according to settings.
          </p>
        </div>
        <div className="card-drawer-footer np-drawer-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </aside>
    </div>
  );
}

// ─── Create Category Drawer ─────────────────────────────────
function CategoryCreateDrawer({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const PRESET_COLORS = ['#6366f1', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#3b82f6'];

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { setError('Category name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await notepadApi.createNotepadCategory({ name: name.trim(), color });
      onCreated();
      setName('');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside className="card-drawer np-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="New Category" style={{ maxWidth: 440 }}>
        <div className="card-drawer-header np-drawer-header">
          <div className="card-drawer-header-left">
            <div className="card-drawer-icon-badge" style={{ background: `${color}20`, color }}>
              <Folder size={18} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">New Category</h2>
              <p className="card-drawer-subtitle">Organize notes into categories</p>
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close drawer"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="card-drawer-body np-drawer-body">
            <div className="np-field-group">
              <label className="np-label">Category Name</label>
              <input
                type="text"
                className="np-input"
                placeholder="e.g. Work, Finance, Ideas..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                maxLength={50}
              />
            </div>
            <div className="np-field-group" style={{ marginTop: 16 }}>
              <label className="np-label">Theme Color</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: c,
                      border: color === c ? '3px solid #ffffff' : '2px solid transparent',
                      boxShadow: color === c ? `0 0 0 2px ${c}` : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  />
                ))}
              </div>
            </div>
            {error && <div className="np-error-msg"><AlertTriangle size={13} /> {error}</div>}
          </div>
          <div className="card-drawer-footer np-drawer-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Category'}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

// ─── Create Template Drawer ─────────────────────────────────
function TemplateCreateDrawer({ open, categories, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { setError('Template name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await notepadApi.createNotepadTemplate({
        name: name.trim(),
        category: category || 'General',
        description: description.trim(),
        content: content,
      });
      onCreated();
      setName('');
      setDescription('');
      setContent('');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside className="card-drawer np-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="New Template" style={{ maxWidth: 500 }}>
        <div className="card-drawer-header np-drawer-header">
          <div className="card-drawer-header-left">
            <div className="card-drawer-icon-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}>
              <Sparkles size={18} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">New Template</h2>
              <p className="card-drawer-subtitle">Create a reusable template for notes</p>
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close drawer"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="card-drawer-body np-drawer-body">
            <div className="np-field-group">
              <label className="np-label">Template Name</label>
              <input
                type="text"
                className="np-input"
                placeholder="e.g. Daily Standup, Meeting Agenda..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                maxLength={100}
              />
            </div>
            <div className="np-field-row">
              <div className="np-field-group np-field-half">
                <label className="np-label">Category</label>
                <select className="np-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">General</option>
                  {categories.map((c) => (
                    <option key={c.id || c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="np-field-group np-field-half">
                <label className="np-label">Short Description</label>
                <input
                  type="text"
                  className="np-input"
                  placeholder="e.g. Fast memo template"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={150}
                />
              </div>
            </div>
            <div className="np-field-group" style={{ marginTop: 10 }}>
              <label className="np-label">Default Template Content</label>
              <textarea
                className="np-textarea"
                placeholder="Type the outline or starter text for notes using this template..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                style={{ minHeight: 140 }}
              />
            </div>
            {error && <div className="np-error-msg"><AlertTriangle size={13} /> {error}</div>}
          </div>
          <div className="card-drawer-footer np-drawer-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

// ─── Note Card ─────────────────────────────────────────────
function NoteCard({ note, onEdit, onDelete, onFavorite, onShare, onPublicLink, onRestore, isPermanentDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isTrash = !!note.deleted_at;
  const charCount = note.char_count || 0;
  const charPercent = Math.min((charCount / MAX_CHARS) * 100, 100);

  return (
    <div className={`np-card glass-panel ${note.is_favorite ? 'np-card-favorite' : ''}`}>
      <div className="np-card-header">
        <div className="np-card-title-row">
          <h3 className="np-card-title">{note.title || 'Untitled Note'}</h3>
          {note.is_favorite && <Star size={14} color="#f59e0b" fill="#f59e0b" />}
        </div>
        <div className="np-card-badges">
          <span className={`np-visibility-badge ${note.visibility === 'PUBLIC' ? 'np-badge-public' : 'np-badge-private'}`}>
            {note.visibility === 'PUBLIC' ? <Globe size={11} /> : <Lock size={11} />}
            {note.visibility}
          </span>
          {note.category_name && (
            <span className="np-category-badge"><Folder size={11} /> {note.category_name}</span>
          )}
        </div>
      </div>

      <p className="np-card-preview">
        {note.content ? stripHtml(note.content).substring(0, 120) + (stripHtml(note.content).length > 120 ? '...' : '') : <em className="text-secondary">Empty note</em>}
      </p>

      {/* Tags */}
      {note.tags && note.tags.length > 0 && (
        <div className="np-card-tags">
          {note.tags.slice(0, 4).map((t) => <span key={t} className="np-tag-mini">#{t}</span>)}
          {note.tags.length > 4 && <span className="np-tag-mini">+{note.tags.length - 4}</span>}
        </div>
      )}

      {/* Char bar */}
      <div className="np-card-char-row">
        <div className="np-mini-char-bar">
          <div className="np-mini-char-fill" style={{ width: `${charPercent}%` }} />
        </div>
        <span className="np-card-char-count">{charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}</span>
      </div>

      <div className="np-card-footer">
        <span className="text-secondary" style={{ fontSize: '0.77rem' }}>
          {isTrash ? `Deleted ${formatDate(note.deleted_at)}` : `Updated ${formatDate(note.updated_at)}`}
        </span>
        <div className="np-card-actions">
          {isTrash ? (
            <>
              <button type="button" className="btn-icon" title="Restore" onClick={() => onRestore(note)}><RotateCcw size={14} /></button>
              <button type="button" className="btn-icon np-danger-btn" title="Delete Forever" onClick={() => isPermanentDelete(note)}><Trash2 size={14} /></button>
            </>
          ) : (
            <>
              <button type="button" className="btn-icon" title="Favorite" onClick={() => onFavorite(note)}>
                <Star size={14} fill={note.is_favorite ? '#f59e0b' : 'none'} color={note.is_favorite ? '#f59e0b' : undefined} />
              </button>
              {note.visibility === 'PUBLIC' && note.publicLink && (
                <button type="button" className="btn-icon" title="Public Link" onClick={() => onPublicLink(note)}>
                  <Link2 size={14} color="#06b6d4" />
                </button>
              )}
              <button type="button" className="btn-icon" title="Share" onClick={() => onShare(note)}><Share2 size={14} /></button>
              <button type="button" className="btn-icon" title="Edit" onClick={() => onEdit(note)}><Edit3 size={14} /></button>
              <button type="button" className="btn-icon np-danger-btn" title="Delete" onClick={() => onDelete(note)}><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main NotepadApp ────────────────────────────────────────
export default function NotepadApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = useApplications();

  const searchParams = new URLSearchParams(location.search);
  const activeFilter = searchParams.get('filter') || 'all';

  // State
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState('');

  // Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [viewMode, setViewMode] = useState('grid');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Modals & Drawers
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [shareModalNote, setShareModalNote] = useState(null);
  const [publicLinkNote, setPublicLinkNote] = useState(null);
  const [publicLinkData, setPublicLinkData] = useState(null);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmPermDelete, setConfirmPermDelete] = useState(null);

  const canCreate = hasPermission('notepad', 'access');

  // ── Data Loading ──
  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = {
        filter: activeFilter,
        search: searchQuery || undefined,
        sortBy,
        visibility: visibilityFilter || undefined,
        category: categoryFilter || undefined,
      };

      const [notesRes, catsRes, tagsRes, templatesRes] = await Promise.all([
        notepadApi.listNotes(params),
        notepadApi.getNotepadCategories(),
        notepadApi.getNotepadTags(),
        notepadApi.getNotepadTemplates(),
      ]);

      setNotes(notesRes.data?.notes || notesRes.data || []);
      setCategories(catsRes.data || []);
      setTags(tagsRes.data || []);
      setTemplates(templatesRes.data || []);

      if (activeFilter === 'all' || activeFilter === 'dashboard') {
        try {
          const dashRes = await notepadApi.getNotepadDashboard();
          setDashboard(dashRes.data);
        } catch (_) {}
      }
    } catch (e) {
      setLoadError(e?.response?.data?.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchQuery, sortBy, visibilityFilter, categoryFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ── Ensure user has default categories seeded locally ──
  useEffect(() => {
    if (categories.length === 0 && !loading) {
      // Seed defaults silently
      Promise.all(
        DEFAULT_CATEGORIES.map((name) =>
          notepadApi.createNotepadCategory({ name }).catch(() => null)
        )
      ).then(() => notepadApi.getNotepadCategories().then((r) => setCategories(r.data || [])));
    }
  }, [categories.length, loading]);

  // ── Save Note ──
  const handleSaveNote = async (data, noteId) => {
    let result;
    if (noteId) {
      const res = await notepadApi.updateNote(noteId, data);
      result = res.data;
      showToast('Note updated');
    } else {
      const res = await notepadApi.createNote(data);
      result = res.data;
      showToast('Note created');
    }
    loadData();
    return result;
  };

  // ── Favorite ──
  const handleFavorite = async (note) => {
    await notepadApi.toggleFavorite(note.id);
    showToast(note.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    loadData();
  };

  // ── Delete ──
  const handleDelete = async (note) => {
    setConfirmDelete(null);
    await notepadApi.deleteNote(note.id);
    showToast('Note moved to trash');
    loadData();
  };

  // ── Restore ──
  const handleRestore = async (note) => {
    await notepadApi.restoreNote(note.id);
    showToast('Note restored');
    loadData();
  };

  // ── Permanent Delete ──
  const handlePermanentDelete = async (note) => {
    setConfirmPermDelete(null);
    await notepadApi.permanentDeleteNote(note.id);
    showToast('Note permanently deleted');
    loadData();
  };

  // ── Public Link ──
  const openPublicLink = (note) => {
    setPublicLinkNote(note);
    setPublicLinkData(null);
  };

  const handleRegenerate = async () => {
    try {
      const res = await notepadApi.createPublicLink(publicLinkNote.id);
      setPublicLinkData(res.data);
      showToast('Public link regenerated');
      loadData();
    } catch (e) {
      showToast('Failed to regenerate link');
    }
  };

  const handleRevokeLink = async () => {
    try {
      await notepadApi.revokePublicLink(publicLinkNote.id);
      setPublicLinkNote(null);
      showToast('Public link disabled');
      loadData();
    } catch (e) {
      showToast('Failed to revoke link');
    }
  };

  // ── Derived ──
  const filteredNotes = useMemo(() => {
    let list = [...notes];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((n) =>
        n.title.toLowerCase().includes(q) ||
        stripHtml(n.content || '').toLowerCase().includes(q) ||
        (n.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [notes, searchQuery]);

  // ─────────────────────────────────────────────────────────
  // PAGE TITLE
  // ─────────────────────────────────────────────────────────
  const getPageTitle = () => {
    switch (activeFilter) {
      case 'dashboard': return 'Dashboard';
      case 'my-notes': return 'My Notes';
      case 'favorites': return 'Favorites';
      case 'recent': return 'Recent';
      case 'public': return 'Public Notes';
      case 'private': return 'Private Notes';
      case 'shared-with-me': return 'Shared With Me';
      case 'shared-by-me': return 'Shared By Me';
      case 'categories': return 'Categories';
      case 'tags': return 'Tags';
      case 'templates': return 'Templates';
      case 'trash': return 'Trash';
      default: return 'All Notes';
    }
  };

  // ─────────────────────────────────────────────────────────
  // DASHBOARD VIEW
  // ─────────────────────────────────────────────────────────
  const renderDashboard = () => (
    <div className="np-dashboard">
      <div className="np-stats-grid">
        {[
          { label: 'Total Notes', value: dashboard?.totalNotes ?? '—', icon: StickyNote, color: '#6366f1' },
          { label: 'Private Notes', value: dashboard?.privateNotes ?? '—', icon: Lock, color: '#10b981' },
          { label: 'Public Notes', value: dashboard?.publicNotes ?? '—', icon: Globe, color: '#06b6d4' },
          { label: 'Favorites', value: dashboard?.favorites ?? '—', icon: Star, color: '#f59e0b' },
          { label: 'Shared With Me', value: dashboard?.sharedWithMe ?? '—', icon: Users, color: '#8b5cf6' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="np-stat-card glass-panel">
            <div className="np-stat-icon" style={{ background: `${color}18`, color }}><Icon size={22} /></div>
            <div>
              <div className="np-stat-value">{value}</div>
              <div className="np-stat-label">{label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="np-dashboard-row">
        {/* Recently Created */}
        <div className="np-recent-section glass-panel">
          <h3 className="np-section-title"><Clock size={15} /> Recently Created</h3>
          {(dashboard?.recentCreated || []).length === 0 ? (
            <p className="text-secondary text-sm">No notes yet. Create your first note!</p>
          ) : (
            dashboard.recentCreated.map((n) => (
              <div key={n.id} className="np-recent-item" onClick={() => { setEditingNote(n); setEditorOpen(true); }}>
                <StickyNote size={13} />
                <span className="np-recent-title">{n.title}</span>
                <span className="text-secondary" style={{ fontSize: '0.75rem', marginLeft: 'auto' }}>{formatDate(n.created_at)}</span>
              </div>
            ))
          )}
        </div>

        {/* Recently Edited */}
        <div className="np-recent-section glass-panel">
          <h3 className="np-section-title"><Pencil size={15} /> Recently Edited</h3>
          {(dashboard?.recentEdited || []).length === 0 ? (
            <p className="text-secondary text-sm">No recent edits.</p>
          ) : (
            dashboard.recentEdited.map((n) => (
              <div key={n.id} className="np-recent-item" onClick={() => { setEditingNote(n); setEditorOpen(true); }}>
                <Edit3 size={13} />
                <span className="np-recent-title">{n.title}</span>
                <span className="text-secondary" style={{ fontSize: '0.75rem', marginLeft: 'auto' }}>{formatDate(n.updated_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  // CATEGORIES VIEW
  // ─────────────────────────────────────────────────────────
  const renderCategories = () => {
    const noteCounts = {};
    notes.forEach((n) => { if (n.category_name) noteCounts[n.category_name] = (noteCounts[n.category_name] || 0) + 1; });
    return (
      <div className="np-categories-view">
        {/* Top Action Bar */}
        <div className="np-categories-top-bar">
          <div className="np-categories-count-label">
            <Folder size={16} />
            <span>{categories.length} Categories</span>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm np-add-cat-btn"
            onClick={() => setCategoryDrawerOpen(true)}
          >
            <Plus size={14} /> Add Category
          </button>
        </div>

        {/* Scrollable 2-column Grid */}
        <div className="np-categories-scroll-container">
          <div className="np-categories-grid">
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="np-category-card glass-panel"
                onClick={() => { setCategoryFilter(cat.name); navigate('/apps/notepad'); }}
              >
                <div
                  className="np-category-card-icon"
                  style={{ background: `${cat.color || '#6366f1'}18`, color: cat.color || '#6366f1' }}
                >
                  <Folder size={18} />
                </div>
                <div className="np-category-card-info">
                  <h4 className="np-category-card-name" title={cat.name}>{cat.name}</h4>
                  <span className="np-category-card-count text-secondary">{noteCounts[cat.name] || 0} notes</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────
  // TAGS VIEW
  // ─────────────────────────────────────────────────────────
  const renderTags = () => {
    const tagCounts = {};
    notes.forEach((n) => (n.tags || []).forEach((t) => { tagCounts[t] = (tagCounts[t] || 0) + 1; }));
    return (
      <div className="np-tags-cloud glass-panel">
        {tags.length === 0 ? (
          <p className="text-secondary text-sm">No tags yet. Add tags when creating notes.</p>
        ) : (
          tags.map((t) => (
            <button key={t.id} type="button" className="np-cloud-tag" onClick={() => navigate(`/apps/notepad?filter=all&tag=${t.name}`)}>
              <span className="np-cloud-tag-name">#{t.name}</span>
              {tagCounts[t.name] ? <span className="np-cloud-tag-count">{tagCounts[t.name]}</span> : null}
            </button>
          ))
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────
  // TEMPLATES VIEW
  // ─────────────────────────────────────────────────────────
  const renderTemplates = () => (
    <div className="np-templates-view">
      {/* Top Action Bar */}
      <div className="np-templates-top-bar">
        <div className="np-templates-count-label">
          <Sparkles size={16} />
          <span>{templates.length} Templates</span>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm np-add-template-btn"
          onClick={() => setTemplateDrawerOpen(true)}
        >
          <Plus size={14} /> Create Template
        </button>
      </div>

      {/* Horizontal Carousel (1 per view on mobile) */}
      <div className="np-templates-horizontal-scroll">
        <div className="np-templates-grid np-templates-horizontal-row">
          {templates.map((tmpl) => {
            const Icon = TEMPLATE_ICONS[tmpl.name] || StickyNote;
            return (
              <div key={tmpl.id} className="np-template-card glass-panel">
                <div className="np-template-card-top-row">
                  <div className="np-template-card-icon"><Icon size={20} color="#6366f1" /></div>
                  {tmpl.category && <span className="np-category-badge"><Folder size={11} /> {tmpl.category}</span>}
                </div>
                <h4 className="np-template-card-title">{tmpl.name}</h4>
                {tmpl.description && <p className="np-template-card-desc text-secondary">{tmpl.description}</p>}
                <div className="np-template-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => {
                    setEditingNote({ content: tmpl.content, title: tmpl.name, tags: [], visibility: 'PRIVATE' });
                    setEditorOpen(true);
                  }}>
                    Use Template
                  </button>
                  {!tmpl.is_system && (
                    <button type="button" className="btn-icon np-danger-btn" title="Delete Template" onClick={async () => {
                      if (window.confirm(`Delete template "${tmpl.name}"?`)) {
                        await notepadApi.deleteNotepadTemplate(tmpl.id);
                        const r = await notepadApi.getNotepadTemplates();
                        setTemplates(r.data || []);
                      }
                    }}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  // NOTES LIST/GRID
  // ─────────────────────────────────────────────────────────
  const renderNotes = () => {
    if (loading) return <div className="np-loading">Loading notes...</div>;
    if (loadError) return <div className="np-error">{loadError}</div>;
    if (filteredNotes.length === 0) return (
      <div className="np-empty-state glass-panel">
        <StickyNote size={40} color="#6366f1" />
        <h3>No notes found</h3>
        <p className="text-secondary">
          {activeFilter === 'trash' ? 'Trash is empty.' : 'Create your first note to get started.'}
        </p>
        {activeFilter !== 'trash' && canCreate && (
          <button type="button" className="btn btn-primary" onClick={() => { setEditingNote(null); setEditorOpen(true); }}>
            <Plus size={15} /> New Note
          </button>
        )}
      </div>
    );

    if (viewMode === 'list') {
      return (
        <div className="np-list-view">
          {filteredNotes.map((note) => (
            <div key={note.id} className="np-list-row glass-panel">
              <div className="np-list-main">
                <div className="np-list-title-row">
                  {note.is_favorite && <Star size={13} color="#f59e0b" fill="#f59e0b" />}
                  <span className="np-list-title">{note.title}</span>
                  <span className={`np-visibility-badge ${note.visibility === 'PUBLIC' ? 'np-badge-public' : 'np-badge-private'}`}>
                    {note.visibility === 'PUBLIC' ? <Globe size={10} /> : <Lock size={10} />} {note.visibility}
                  </span>
                  {note.category_name && <span className="np-category-badge"><Folder size={10} /> {note.category_name}</span>}
                </div>
                <div className="np-list-meta">
                  <span className="text-secondary text-sm">{note.char_count?.toLocaleString()} chars</span>
                  <span className="text-secondary text-sm">·</span>
                  <span className="text-secondary text-sm">Updated {formatDate(note.updated_at)}</span>
                  {(note.tags || []).slice(0, 3).map((t) => <span key={t} className="np-tag-mini">#{t}</span>)}
                </div>
              </div>
              <div className="np-list-actions">
                {activeFilter === 'trash' ? (
                  <>
                    <button type="button" className="btn-icon" onClick={() => handleRestore(note)}><RotateCcw size={14} /></button>
                    <button type="button" className="btn-icon np-danger-btn" onClick={() => setConfirmPermDelete(note)}><Trash2 size={14} /></button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn-icon" onClick={() => handleFavorite(note)}>
                      <Star size={13} fill={note.is_favorite ? '#f59e0b' : 'none'} color={note.is_favorite ? '#f59e0b' : undefined} />
                    </button>
                    {note.visibility === 'PUBLIC' && note.publicLink && (
                      <button type="button" className="btn-icon" onClick={() => openPublicLink(note)}><Link2 size={13} color="#06b6d4" /></button>
                    )}
                    <button type="button" className="btn-icon" onClick={() => setShareModalNote(note)}><Share2 size={13} /></button>
                    <button type="button" className="btn-icon" onClick={() => { setEditingNote(note); setEditorOpen(true); }}><Edit3 size={13} /></button>
                    <button type="button" className="btn-icon np-danger-btn" onClick={() => setConfirmDelete(note)}><Trash2 size={13} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="np-notes-grid">
        {filteredNotes.map((note) => (
          <NoteCard
            key={note.id}
            note={note}
            onEdit={(n) => { setEditingNote(n); setEditorOpen(true); }}
            onDelete={(n) => setConfirmDelete(n)}
            onFavorite={handleFavorite}
            onShare={(n) => setShareModalNote(n)}
            onPublicLink={(n) => openPublicLink(n)}
            onRestore={handleRestore}
            isPermanentDelete={(n) => setConfirmPermDelete(n)}
          />
        ))}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <MainLayout>
      {/* Toast */}
      {toast && (
        <div className="notes-toast animate-slide-up">
          <CheckCircle2 size={15} className="text-success" />
          <span>{toast}</span>
        </div>
      )}

      {/* Header - Full Bleed Edge-to-Edge with Multicolor Gradient */}
      <div className="notes-module-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="notes-header-left">
            <div className="notes-app-icon-badge notes-hero-icon-badge" style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}>
              <StickyNote size={26} />
            </div>
            <div>
              <div className="notes-header-title-row">
                <h1 className="notes-page-title notes-hero-title">{getPageTitle()}</h1>
              </div>
              <p className="notes-page-subtitle notes-hero-subtitle">
                {activeFilter === 'trash'
                  ? `${filteredNotes.length} note(s) in trash`
                  : `${filteredNotes.length} note(s) · 10,000 character limit`}
              </p>
            </div>
          </div>
          <div className="notes-header-actions">
            {activeFilter !== 'trash' && canCreate && (
              <button type="button" className="btn btn-primary doc-header-btn notes-hero-btn-primary" onClick={() => { setEditingNote(null); setEditorOpen(true); }}>
                <Plus size={16} /> New Note
              </button>
            )}
            <button type="button" className="btn-icon doc-refresh-btn notes-hero-btn-secondary" onClick={loadData} title="Refresh">
              <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="notes-app-container animate-fade-in">
        {/* Main Content by filter */}
        {activeFilter === 'dashboard' ? renderDashboard() :
         activeFilter === 'categories' ? renderCategories() :
         activeFilter === 'tags' ? renderTags() :
         activeFilter === 'templates' ? renderTemplates() : (
          <>
            {/* Desktop Toolbar (> 640px) */}
            <div className="notepad-desktop-toolbar notes-toolbar doc-toolbar">
              <div className="notes-toolbar-left doc-toolbar-left">
                <div className="notes-search-box doc-search-box">
                  <Search size={15} className="search-icon" />
                  <input
                    type="text"
                    className="notes-search-input"
                    placeholder="Search notes, tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button type="button" className="notes-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
                  )}
                </div>
                <select className="notes-select doc-select" value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)}>
                  <option value="">All Visibility</option>
                  <option value="PRIVATE">Private</option>
                  <option value="PUBLIC">Public</option>
                </select>
                <select className="notes-select doc-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="notes-toolbar-right doc-toolbar-right">
                <select className="notes-select doc-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="updated_desc">Recently Updated</option>
                  <option value="created_desc">Recently Created</option>
                  <option value="title_asc">Title (A-Z)</option>
                  <option value="title_desc">Title (Z-A)</option>
                </select>
                <div className="notes-view-mode-switch">
                  <button type="button" className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid"><LayoutGrid size={15} /></button>
                  <button type="button" className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="List"><ListIcon size={15} /></button>
                </div>
              </div>
            </div>

            {/* Mobile Filter Bar (<= 640px) - Single Row Icon-Only Bar */}
            <div className="notepad-mobile-filter-bar tasks-mobile-filter-bar">
              {/* Search Toggle Icon */}
              <button
                type="button"
                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                className={`mobile-filter-icon-btn ${isMobileSearchOpen || searchQuery ? 'active' : ''}`}
                title="Search notes"
                aria-label="Toggle search input"
              >
                <Search size={16} />
              </button>

              {/* Visibility Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${visibilityFilter ? 'active' : ''}`}
                title={`Visibility: ${visibilityFilter || 'All'}`}
              >
                {visibilityFilter === 'PUBLIC' ? <Globe size={16} /> : <Lock size={16} />}
                <select
                  value={visibilityFilter}
                  onChange={(e) => setVisibilityFilter(e.target.value)}
                  aria-label="Filter by visibility"
                >
                  <option value="">All Visibility</option>
                  <option value="PRIVATE">Private</option>
                  <option value="PUBLIC">Public</option>
                </select>
              </div>

              {/* Category Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${categoryFilter ? 'active' : ''}`}
                title={`Category: ${categoryFilter || 'All'}`}
              >
                <Folder size={16} />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id || c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Sort Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${sortBy !== 'updated_desc' ? 'active' : ''}`}
                title="Sort notes"
              >
                <ArrowUpDown size={16} />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort notes"
                >
                  <option value="updated_desc">Recently Updated</option>
                  <option value="created_desc">Recently Created</option>
                  <option value="title_asc">Title (A-Z)</option>
                  <option value="title_desc">Title (Z-A)</option>
                </select>
              </div>

              {/* View Mode Toggle (Grid / List) */}
              <div className="mobile-view-mode-pill">
                <button
                  type="button"
                  className={`mobile-view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <LayoutGrid size={15} />
                </button>
                <button
                  type="button"
                  className={`mobile-view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <ListIcon size={15} />
                </button>
              </div>

              {/* Reset Filters Icon Button (Shown when active) */}
              {(searchQuery || visibilityFilter || categoryFilter || sortBy !== 'updated_desc') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setVisibilityFilter('');
                    setCategoryFilter('');
                    setSortBy('updated_desc');
                    setIsMobileSearchOpen(false);
                  }}
                  className="mobile-filter-icon-btn reset-btn"
                  title="Reset all filters"
                  aria-label="Reset filters"
                >
                  <X size={15} />
                </button>
              )}

              {/* Refresh Icon Button */}
              <button
                type="button"
                onClick={loadData}
                className="mobile-filter-icon-btn"
                title="Refresh notes"
                aria-label="Refresh notes"
              >
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Expandable Mobile Search Bar */}
            {isMobileSearchOpen && (
              <div className="notepad-mobile-search-expand tasks-mobile-search-expand animate-slide-down">
                <Search size={15} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search notes, tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="clear-search-btn"
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}

            {renderNotes()}
          </>
        )}

        {/* Modals & Drawers */}
        {editorOpen && (
          <NoteEditorModal
            note={editingNote}
            categories={categories}
            templates={templates}
            onSave={handleSaveNote}
            onClose={() => { setEditorOpen(false); setEditingNote(null); }}
          />
        )}

        {shareModalNote && (
          <ShareModal
            note={shareModalNote}
            onClose={() => setShareModalNote(null)}
          />
        )}

        {publicLinkNote && (
          <PublicLinkModal
            note={publicLinkNote}
            link={publicLinkData}
            onClose={() => { setPublicLinkNote(null); setPublicLinkData(null); }}
            onRegenerate={handleRegenerate}
            onRevoke={handleRevokeLink}
          />
        )}

        {categoryDrawerOpen && (
          <CategoryCreateDrawer
            open={categoryDrawerOpen}
            onClose={() => setCategoryDrawerOpen(false)}
            onCreated={() => {
              notepadApi.getNotepadCategories().then((r) => setCategories(r.data || []));
              showToast('Category created');
            }}
          />
        )}

        {templateDrawerOpen && (
          <TemplateCreateDrawer
            open={templateDrawerOpen}
            categories={categories}
            onClose={() => setTemplateDrawerOpen(false)}
            onCreated={() => {
              notepadApi.getNotepadTemplates().then((r) => setTemplates(r.data || []));
              showToast('Template created');
            }}
          />
        )}

        {/* Confirm Delete */}
        {confirmDelete && (
          <div className="np-modal-overlay" onClick={() => setConfirmDelete(null)}>
            <div className="np-confirm-box glass-panel" onClick={(e) => e.stopPropagation()}>
              <AlertTriangle size={24} color="#ef4444" />
              <h3>Move to Trash?</h3>
              <p className="text-secondary">"{confirmDelete.title}" will be moved to trash. You can restore it later.</p>
              <div className="np-confirm-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={() => handleDelete(confirmDelete)}>Move to Trash</button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Permanent Delete */}
        {confirmPermDelete && (
          <div className="np-modal-overlay" onClick={() => setConfirmPermDelete(null)}>
            <div className="np-confirm-box glass-panel" onClick={(e) => e.stopPropagation()}>
              <AlertTriangle size={24} color="#ef4444" />
              <h3>Delete Permanently?</h3>
              <p className="text-secondary">This action cannot be undone. "{confirmPermDelete.title}" will be permanently deleted.</p>
              <div className="np-confirm-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmPermDelete(null)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={() => handlePermanentDelete(confirmPermDelete)}>Delete Forever</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
